import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { File, Search, Download, Trash2, Folder, Calendar, User, FileText } from 'lucide-react';
import { AuthContext } from '../App';
import { getFileIcon, getFileExtension } from '../utils/fileIcons';

const API = window.location.origin || 'http://localhost:8001';
axios.defaults.withCredentials = true;

function Files() {
  const { user } = useContext(AuthContext);
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const debounceRef = useRef(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Güvenli total değeri (undefined olma ihtimaline karşı)
  const safeTotal = total || 0;

  // Debounced search - 400ms bekle, sonra API çağrısı yap
  const handleSearchInput = useCallback((value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 400);
  }, []);

  useEffect(() => {
    fetchFiles();
    fetchProjects();
  }, [page, selectedProject, searchQuery]);

  // filterFiles artık gereksiz - backend'de arama yapılıyor
  useEffect(() => {
    // files değiştiğinde filteredFiles'ı güncelle (artık backend'den filtrelenmiş geliyor)
    setFilteredFiles(files);
  }, [files]);

  // Search değiştiğinde sayfa 1'e dön
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedProject]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit
      };
      if (selectedProject !== 'all') {
        params.project_id = selectedProject;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await axios.get(`${API}/api/files`, { params });
      
      // Backend artık object döndürüyor: { files, total, page, limit, totalPages }
      // Güvenli kontroller
      setFiles(response.data.files || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const filterFiles = () => {
    // Backend'den zaten filtrelenmiş dosyalar geliyor
    // Sadece grouping yapıyoruz (aynı dosyanın birden fazla göreve eklenmiş olması durumu)
    
    const groupedFiles = {};
    files.forEach(file => {
      const key = file.s3Key || file.id; // s3Key yoksa id kullan
      
      if (!groupedFiles[key]) {
        groupedFiles[key] = {
          ...file,
          tasks: [{ 
            taskId: file.taskId, 
            taskTitle: file.taskTitle, 
            projectName: file.projectName,
            projectId: file.projectId 
          }],
          allFileIds: [file.id] // Tüm file ID'leri sakla (silme için)
        };
      } else {
        // Aynı s3Key'e sahip başka bir kayıt
        groupedFiles[key].tasks.push({ 
          taskId: file.taskId, 
          taskTitle: file.taskTitle,
          projectName: file.projectName,
          projectId: file.projectId
        });
        groupedFiles[key].allFileIds.push(file.id);
      }
    });

    const uniqueFiles = Object.values(groupedFiles);
    setFilteredFiles(uniqueFiles);
  };

  const handleDelete = async (fileId, uploadedBy) => {
    // Yetki kontrolü - canDelete fonksiyonu ile tutarlı
    if (!canDelete(uploadedBy)) {
      alert('Bu dosyayı silme yetkiniz yok!');
      return;
    }

    if (!window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/files/${fileId}`);
      fetchFiles(); // Listeyi yenile
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Dosya silinirken bir hata oluştu');
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      // Invisible link ile direkt indirme - yeni pencere açmaz
      const downloadUrl = `${API}/api/files/${fileId}/download?token=${localStorage.getItem('session_token') || ''}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || '';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Dosya indirilirken bir hata oluştu');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const canDelete = (uploadedBy) => {
    // can_delete_files iznine sahip olanlar silebilir
    return user.permissions?.includes('can_delete_files');
  };

  // Toplu seçim fonksiyonları
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFiles([]);
      setSelectAll(false);
    } else {
      // Sadece silinebilir dosyaları seç
      const selectableFiles = filteredFiles.filter(file => canDelete(file.uploadedBy));
      setSelectedFiles(selectableFiles.map(f => f.id));
      setSelectAll(true);
    }
  };

  const handleSelectFile = (fileId, uploadedBy) => {
    // Yetki kontrolü
    if (!canDelete(uploadedBy)) {
      alert('Bu dosyayı silme yetkiniz yok!');
      return;
    }

    if (selectedFiles.includes(fileId)) {
      setSelectedFiles(selectedFiles.filter(id => id !== fileId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedFiles, fileId];
      setSelectedFiles(newSelected);
      
      // Tüm seçilebilir dosyalar seçildiyse selectAll'u true yap
      const selectableFiles = filteredFiles.filter(file => canDelete(file.uploadedBy));
      if (newSelected.length === selectableFiles.length) {
        setSelectAll(true);
      }
    }
  };

  // Toplu silme
  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;

    if (!window.confirm(`${selectedFiles.length} dosyayı silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      // Paralel olarak tüm dosyaları sil
      const deletePromises = selectedFiles.map(fileId => 
        axios.delete(`${API}/api/files/${fileId}`)
      );

      await Promise.all(deletePromises);
      
      alert(`${selectedFiles.length} dosya başarıyla silindi`);
      setSelectedFiles([]);
      setSelectAll(false);
      fetchFiles(); // Listeyi yenile
    } catch (error) {
      console.error('Error deleting files:', error);
      alert('Dosyalar silinirken bir hata oluştu');
    }
  };

  // Filtreleme değiştiğinde seçimleri temizle
  useEffect(() => {
    setSelectedFiles([]);
    setSelectAll(false);
  }, [selectedProject, searchQuery]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <span className="relative">
            Dosyalar
            {/* Toplam dosya sayısı badge */}
            <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
              {safeTotal.toLocaleString('tr-TR')}
            </span>
          </span>
        </h1>
        <p className="text-gray-600 mt-2 text-sm sm:text-base">
          Projelere yüklenmiş tüm dosyalar
          {safeTotal > 0 && (
            <span className="text-blue-600 font-medium text-xs sm:text-sm"> • Toplam {safeTotal.toLocaleString('tr-TR')} dosya</span>
          )}
        </p>
      </div>

      {/* Filtreler */}
      <div className="glass rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Arama */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Dosya, görev veya proje ara..."
              value={searchInput}
              onChange={(e) => handleSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          {/* Proje Filtresi */}
          <div className="relative z-10">
            <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none z-10" />
            <select
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setPage(1); // Proje değiştiğinde sayfa 1'e dön
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white relative z-20"
            >
              <option value="all">Tüm Projeler</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sonuç sayısı ve Toplu Silme Butonu */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {filteredFiles.length} dosya listelendi 
            {safeTotal > filteredFiles.length && (
              <span className="text-gray-500"> (Toplam: {safeTotal.toLocaleString('tr-TR')} dosya)</span>
            )}
          </div>
          
          {/* Toplu Silme Butonu */}
          {selectedFiles.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Trash2 className="w-4 h-4" />
              <span className="font-medium">{selectedFiles.length} dosya seçildi - Seçilenleri Sil</span>
            </button>
          )}
        </div>
      </div>

      {/* Dosya Listesi */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <p className="mt-2 text-gray-600">Yükleniyor...</p>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Dosya bulunamadı</p>
        </div>
      ) : (
        <>
          {/* Desktop Tablo Görünümü - lg ve üzeri (1024px+) - KOMPAKT VERSİYON */}
          <div className="hidden lg:block glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer"
                        title="Tümünü Seç/Kaldır"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dosya Bilgisi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Yükleyen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bilgi
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFiles.map((file) => {
                    const isSelected = selectedFiles.includes(file.id);
                    const isDeletable = canDelete(file.uploadedBy);
                    
                    return (
                      <tr 
                        key={file.id} 
                        className={`transition-colors ${isSelected ? 'bg-cyan-50' : 'hover:bg-gray-50'}`}
                      >
                        {/* Checkbox */}
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectFile(file.id, file.uploadedBy)}
                            disabled={!isDeletable}
                            className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            title={isDeletable ? 'Seç' : 'Bu dosyayı silme yetkiniz yok'}
                          />
                        </td>
                        
                        {/* Dosya Bilgisi - Proje + Dosya Adı + Görev */}
                        <td className="px-6 py-4 max-w-md">
                        <div className="space-y-0.5">
                          <div className="flex items-center text-xs text-cyan-600">
                            <Folder className="w-3 h-3 mr-1" />
                            <span className="font-medium">
                              {file.tasks && file.tasks.length > 0 
                                ? file.tasks[0].projectName 
                                : file.projectName || 'Proje yok'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const { Icon: FileIcon, color, bgColor } = getFileIcon(file.filename);
                              const extension = getFileExtension(file.filename);
                              
                              return (
                                <div className="relative">
                                  <div className={`w-8 h-8 bg-gradient-to-br ${bgColor} rounded-lg flex items-center justify-center shadow-sm`}>
                                    <FileIcon className={`w-4 h-4 ${color}`} />
                                  </div>
                                  {extension && (
                                    <div className="absolute -bottom-0.5 -right-0.5 text-[6px] font-bold px-1 py-0.5 rounded bg-white text-gray-600 border border-gray-200">
                                      {extension.toUpperCase()}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            <span className="text-sm font-medium text-gray-900 overflow-hidden whitespace-nowrap text-ellipsis max-w-xs block" title={file.filename}>{file.filename}</span>
                            {file.tasks && file.tasks.length > 1 && (
                              <span className="flex-shrink-0 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 px-2 py-0.5 rounded-md">
                                {file.tasks.length} görevde
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 pl-6 overflow-hidden whitespace-nowrap text-ellipsis">
                            Görev: {file.tasks && file.tasks.length > 0 
                              ? file.tasks.map(t => t.taskTitle).join(', ')
                              : file.taskTitle}
                          </div>
                        </div>
                      </td>
                      
                      {/* Yükleyen */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">{file.uploaderName}</span>
                        </div>
                      </td>
                      
                      {/* Bilgi - Boyut + Tarih (Alt Alta) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-600">
                            {formatFileSize(file.fileSize)}
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            <span>{new Date(file.createdAt).toLocaleDateString('tr-TR')}</span>
                          </div>
                        </div>
                      </td>
                      
                      {/* İşlemler */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleDownload(file.id, file.filename)}
                            className="text-cyan-600 hover:text-cyan-900 p-2 rounded-lg hover:bg-cyan-50 transition-colors"
                            title="İndir"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {canDelete(file.uploadedBy) && (
                            <button
                              onClick={() => handleDelete(file.id, file.uploadedBy)}
                              className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobil/Tablet Kart Görünümü - lg altı (< 1024px) */}
          <div className="lg:hidden space-y-4">
            {filteredFiles.map((file) => {
              const isSelected = selectedFiles.includes(file.id);
              const isDeletable = canDelete(file.uploadedBy);
              
              return (
                <div 
                  key={file.id} 
                  className={`glass rounded-2xl p-4 transition-all ${isSelected ? 'ring-2 ring-cyan-500 bg-cyan-50' : ''}`}
                >
                  {/* Checkbox ve Dosya Adı */}
                  <div className="flex items-start justify-between mb-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1 mr-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectFile(file.id, file.uploadedBy)}
                        disabled={!isDeletable}
                        className="w-5 h-5 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        title={isDeletable ? 'Seç' : 'Bu dosyayı silme yetkiniz yok'}
                      />
                    </div>
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    {(() => {
                      const { Icon: FileIcon, color, bgColor } = getFileIcon(file.filename);
                      const extension = getFileExtension(file.filename);
                      
                      return (
                        <div className="relative flex-shrink-0">
                          <div className={`w-10 h-10 bg-gradient-to-br ${bgColor} rounded-lg flex items-center justify-center shadow-sm`}>
                            <FileIcon className={`w-5 h-5 ${color}`} />
                          </div>
                          {extension && (
                            <div className="absolute -bottom-1 -right-1 text-[7px] font-bold px-1 py-0.5 rounded bg-white text-gray-600 border border-gray-200 shadow-sm">
                              {extension.toUpperCase()}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate" title={file.filename}>{file.filename}</p>
                        {file.tasks && file.tasks.length > 1 && (
                          <span className="flex-shrink-0 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 px-2 py-0.5 rounded-md">
                            {file.tasks.length} görevde
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{formatFileSize(file.fileSize)}</p>
                    </div>
                  </div>
                  {/* İşlemler */}
                  <div className="flex items-center space-x-1 ml-2">
                    <button
                      onClick={() => handleDownload(file.id, file.filename)}
                      className="text-cyan-600 hover:text-cyan-900 p-2 rounded-lg hover:bg-cyan-50 transition-colors"
                      title="İndir"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {canDelete(file.uploadedBy) && (
                      <button
                        onClick={() => handleDelete(file.id, file.uploadedBy)}
                        className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Detaylar */}
                <div className="space-y-2 text-sm">
                  {/* Proje */}
                  {file.tasks && file.tasks.length > 0 && (
                    <div className="flex items-center text-gray-600">
                      <Folder className="w-4 h-4 text-cyan-600 mr-2 flex-shrink-0" />
                      <span className="truncate">{file.tasks[0].projectName || 'Proje yok'}</span>
                    </div>
                  )}
                  
                  {/* Görevler - virgülle ayrılmış */}
                  {file.tasks && file.tasks.length > 0 && (
                    <div className="flex items-start text-gray-600">
                      <span className="text-xs font-medium text-gray-500 mr-2 flex-shrink-0 mt-0.5">Görev:</span>
                      <span className="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                        {file.tasks.map(t => t.taskTitle).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Yükleyen */}
                  <div className="flex items-center text-gray-600">
                    <User className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="truncate">{file.uploaderName}</span>
                  </div>
                  
                  {/* Tarih */}
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{new Date(file.createdAt).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Pagination */}
          {!loading && filteredFiles.length > 0 && totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Önceki
              </button>
              
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">
                  {page} / {totalPages}
                </span>
                <span className="text-gray-500 text-sm">
                  ({safeTotal} dosya)
                </span>
              </div>
              
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  page === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Files;
